import { useEffect, useState } from "react";
import { IntegrationPage, toMessage } from "../components/integration-shell";
import { ApiUnavailableState, LoadingState } from "../components/states";
import { DeadLetterList, ExecutionHistoryTable } from "../components/integrations";
import {
  api,
  WORKFLOW_TYPE_NAMES,
  type WorkflowExecutionView,
  type WorkflowFailureView,
} from "../lib/api";

/**
 * Workflow execution history + dead-letter (section 31). Every field shown is a
 * SAFE projection: workflow type, status, attempts, correlation (source event
 * id), timings and a SAFE error string. No raw secrets and no full private
 * payloads are ever displayed. Replay is only offered for eligible failures; an
 * unsafe (security-rejected) callback can never be replayed.
 */
export function WorkflowHistory(): JSX.Element {
  return (
    <IntegrationPage title="Workflow execution history" intro={<p>Inspect workflow runs and retry eligible failures safely. Only safe error summaries are shown — never raw payloads or secrets.</p>}>
      {(tenantId) => <WorkflowHistoryPanel tenantId={tenantId} />}
    </IntegrationPage>
  );
}

function WorkflowHistoryPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const [workflowType, setWorkflowType] = useState<string>("");
  const [executions, setExecutions] = useState<WorkflowExecutionView[] | null>(null);
  const [failures, setFailures] = useState<WorkflowFailureView[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    Promise.all([
      api.integrations.listExecutions(tenantId, { workflowType: workflowType || undefined, limit: 50 }),
      api.integrations.listDeadLetter(tenantId, { limit: 50 }),
    ])
      .then(([ex, dl]) => {
        setExecutions(ex.items);
        setFailures(dl.failures);
        setError(null);
      })
      .catch((e) => setError(toMessage(e)));
  };

  useEffect(() => {
    load();
    // load reads tenantId + workflowType it depends on
  }, [tenantId, workflowType]);

  const retry = async (failure: WorkflowFailureView) => {
    setBusyId(failure.id);
    setMessage(null);
    try {
      const r = await api.integrations.retryFailure(tenantId, failure.id);
      setMessage(`Replay requested for ${failure.workflowType} (now ${r.state}). A worker job was enqueued — nothing was delivered here.`);
      load();
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="ob-builder">
      <label className="field">
        <span className="field__label">Filter by workflow type</span>
        <select className="field__input" value={workflowType} onChange={(e) => setWorkflowType(e.target.value)} aria-label="Filter by workflow type">
          <option value="">All workflow types</option>
          {WORKFLOW_TYPE_NAMES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>

      {message ? (
        <p className="muted" role="status" style={{ marginBottom: "0.5rem" }}>
          {message}
        </p>
      ) : null}

      {error ? (
        <ApiUnavailableState detail={error} />
      ) : executions === null || failures === null ? (
        <LoadingState label="Reading workflow history…" />
      ) : (
        <>
          <div className="world-panel world-panel--current">
            <h4 className="section-title" style={{ marginTop: 0 }}>
              Execution history
            </h4>
            <ExecutionHistoryTable executions={executions} />
          </div>

          <div className="world-panel world-panel--proposed">
            <h4 className="section-title" style={{ marginTop: 0 }}>
              Dead-letter items
            </h4>
            <p className="muted">Retry re-enqueues an eligible failure for a safe replay with its original idempotency key. Security-rejected callbacks are never replayable.</p>
            <DeadLetterList failures={failures} onRetry={retry} busyId={busyId} />
          </div>
        </>
      )}
    </div>
  );
}
